import { Suspense } from "react";
import { useLazyLoadQuery } from "react-relay";
import { useSearchParams } from "react-router";
import { graphql } from "relay-runtime";

import {
  Button,
  DialogTrigger,
  Flex,
  Icon,
  Icons,
  Loading,
  Modal,
  ModalOverlay,
  Text,
  Tooltip,
  TooltipTrigger,
  TriggerWrap,
  View,
} from "@phoenix/components";
import { EditSpanAnnotationsDialog } from "@phoenix/components/trace/EditSpanAnnotationsDialog";
import { LatencyText } from "@phoenix/components/trace/LatencyText";
import { SpanTokenCosts } from "@phoenix/components/trace/SpanTokenCosts";
import { SpanTokenCount } from "@phoenix/components/trace/SpanTokenCount";
import { SELECTED_SPAN_NODE_ID_PARAM } from "@phoenix/constants/searchParams";

import type { RunMetadataFooterQuery } from "./__generated__/RunMetadataFooterQuery.graphql";
import { PlaygroundRunTraceDetailsDialog } from "./PlaygroundRunTraceDialog";

export function RunMetadataFooter({ spanId }: { spanId: string }) {
  const [, setSearchParams] = useSearchParams();
  const data = useLazyLoadQuery<RunMetadataFooterQuery>(
    graphql`
      query RunMetadataFooterQuery($spanId: ID!) {
        span: node(id: $spanId) {
          id
          ... on Span {
            spanId
            trace {
              id
              traceId
              project {
                id
              }
            }
            tokenCountTotal
            latencyMs
            costSummary {
              total {
                cost
              }
            }
            spanAnnotations {
              id
              name
              label
              score
              explanation
            }
          }
        }
      }
    `,
    { spanId },
    {
      fetchPolicy: "store-and-network",
    }
  );
  if (!data.span || !data.span.trace || !data.span.trace.project) {
    return null;
  }
  const { trace, spanAnnotations } = data.span;
  const totalCost = data.span.costSummary?.total?.cost;

  return (
    <View
      borderTopColor="default"
      borderTopWidth="thin"
      paddingStart="size-200"
      paddingEnd="size-200"
      paddingTop="size-100"
      paddingBottom="size-100"
    >
      <Flex direction="row" gap="size-200" justifyContent="space-between">
        <Flex direction="row" gap="size-150" alignItems="center" wrap="wrap">
          <LatencyText size="S" latencyMs={data.span.latencyMs || 0} />
          <SpanTokenCount
            tokenCountTotal={data.span.tokenCountTotal || 0}
            nodeId={data.span.id}
            size="S"
          />
          {totalCost != null && (
            <SpanTokenCosts
              totalCost={totalCost}
              spanNodeId={data.span.id}
              size="S"
            />
          )}
          {spanAnnotations && spanAnnotations.map((annotation) => {
            if (!annotation) return null;
            const displayName = annotation.name.replace(/_/g, " ");
            
            const isError = 
              annotation.label?.toLowerCase() === "error" || 
              annotation.label?.toLowerCase() === "failed" || 
              String(annotation.explanation || "").toLowerCase().includes("error") ||
              String(annotation.explanation || "").toLowerCase().includes("llm client not configured");

            const displayValue = isError
              ? "0"
              : (annotation.label || (annotation.score != null ? annotation.score.toString() : ""));
            
            // Score color coding
            let badgeBg = "var(--global-color-gray-150)";
            let badgeText = "var(--global-color-gray-800)";
            if (isError) {
              badgeBg = "var(--global-color-red-100)";
              badgeText = "var(--global-color-red-800)";
            } else if (annotation.score != null) {
              if (annotation.score >= 80) {
                badgeBg = "var(--global-color-green-100)";
                badgeText = "var(--global-color-green-800)";
              } else if (annotation.score >= 50) {
                badgeBg = "var(--global-color-orange-100)";
                badgeText = "var(--global-color-orange-800)";
              } else {
                badgeBg = "var(--global-color-red-100)";
                badgeText = "var(--global-color-red-800)";
              }
            } else if (annotation.label) {
              const lowerLabel = annotation.label.toLowerCase();
              if (["correct", "pass", "good", "yes"].includes(lowerLabel)) {
                badgeBg = "var(--global-color-green-100)";
                badgeText = "var(--global-color-green-800)";
              } else if (["incorrect", "fail", "bad", "no", "toxic"].includes(lowerLabel)) {
                badgeBg = "var(--global-color-red-100)";
                badgeText = "var(--global-color-red-800)";
              }
            }
            
            return (
              <TooltipTrigger key={annotation.id}>
                <TriggerWrap>
                  <View
                    paddingX="size-100"
                    paddingY="size-25"
                    borderRadius="medium"
                    css={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontSize: "11px",
                      fontWeight: 600,
                      backgroundColor: badgeBg,
                      color: badgeText,
                      border: "1px solid rgba(0,0,0,0.08)",
                      cursor: "default",
                      userSelect: "none",
                    }}
                  >
                    {displayName}: {displayValue}
                  </View>
                </TriggerWrap>
                {annotation.explanation && (
                  <Tooltip>
                    <Text>{annotation.explanation}</Text>
                  </Tooltip>
                )}
              </TooltipTrigger>
            );
          })}
        </Flex>
        <Flex direction="row" gap="size-100" alignItems="center">
          <DialogTrigger>
            <Button
              size="S"
              leadingVisual={<Icon svg={<Icons.EditOutline />} />}
            >
              Annotate
            </Button>
            <ModalOverlay>
              <Modal variant="slideover" size="S">
                <EditSpanAnnotationsDialog
                  spanNodeId={spanId}
                  projectId={trace.project.id}
                />
              </Modal>
            </ModalOverlay>
          </DialogTrigger>
          <DialogTrigger
            onOpenChange={(open) => {
              if (!open) {
                setSearchParams((searchParams) => {
                  searchParams.delete(SELECTED_SPAN_NODE_ID_PARAM);
                  return searchParams;
                });
              }
            }}
          >
            <Button size="S" leadingVisual={<Icon svg={<Icons.Trace />} />}>
              View Trace
            </Button>
            <ModalOverlay>
              <Modal variant="slideover" size="fullscreen">
                <Suspense fallback={<Loading />}>
                  <PlaygroundRunTraceDetailsDialog
                    traceId={trace.traceId}
                    projectId={trace.project.id}
                    title={`Playground Trace`}
                  />
                </Suspense>
              </Modal>
            </ModalOverlay>
          </DialogTrigger>
        </Flex>
      </Flex>
    </View>
  );
}
